import type { Jogador, Posicao } from './tipos';

/**
 * Funções de campo detalhadas. O motor de partida e a escalação continuam trabalhando com as 5 posições do Brasfoot
 * (G, Z, L, M, A); a função refina cada jogador dentro da posição dele (volante x meia armador, ponta x centroavante...)
 * e muda os pesos das habilidades na força (ver habilidades.ts). Nos elencos reais a função vem da fonte quando existe
 * (ex.: "V" = volante); nos demais é deduzida do lado e das características, sempre igual para o mesmo jogador.
 */
export type Funcao = 'GOL' | 'ZAG' | 'LD' | 'LE' | 'VOL' | 'MC' | 'MEI' | 'PE' | 'PD' | 'SA' | 'CA';

export const FUNCOES: readonly Funcao[] = ['GOL', 'ZAG', 'LD', 'LE', 'VOL', 'MC', 'MEI', 'PE', 'PD', 'SA', 'CA'];

export const NOME_FUNCAO: Record<Funcao, string> = {
  GOL: 'Goleiro',
  ZAG: 'Zagueiro',
  LD: 'Lateral-direito',
  LE: 'Lateral-esquerdo',
  VOL: 'Volante',
  MC: 'Meio-campista',
  MEI: 'Meia armador',
  PE: 'Ponta-esquerda',
  PD: 'Ponta-direita',
  SA: 'Segundo atacante',
  CA: 'Centroavante',
};

/** Posição do jogo (G/Z/L/M/A) a que cada função pertence. */
export const POSICAO_DA_FUNCAO: Record<Funcao, Posicao> = {
  GOL: 'G',
  ZAG: 'Z',
  LD: 'L',
  LE: 'L',
  VOL: 'M',
  MC: 'M',
  MEI: 'M',
  PE: 'A',
  PD: 'A',
  SA: 'A',
  CA: 'A',
};

/** Tem alguma das duas características (sem alocar, pois roda a cada cálculo de força na partida). */
function tem(j: Jogador, a: number, b = -1): boolean {
  const [x, y] = j.caracteristicas;
  return x === a || y === a || x === b || y === b;
}

/** Função do jogador: a gravada (dado real) ou, na falta dela, a deduzida da posição, do lado e das características. */
export function funcaoDe(j: Jogador): Funcao {
  if (j.funcao && POSICAO_DA_FUNCAO[j.funcao] === j.posicao) return j.funcao;
  switch (j.posicao) {
    case 'G':
      return 'GOL';
    case 'Z':
      return 'ZAG';
    case 'L':
      return j.lado === 'E' ? 'LE' : 'LD';
    case 'M':
      // Desarme (7) e Marcação (10) = volante; Armação (4), Drible (8) e Passe (11) com Armação = meia armador
      if (tem(j, 7, 10)) return 'VOL';
      if (tem(j, 4, 8)) return 'MEI';
      return 'MC';
    case 'A':
      if (tem(j, 13, 8) && !tem(j, 5)) return j.lado === 'E' ? 'PE' : 'PD';
      if (tem(j, 4, 11)) return 'SA';
      return 'CA';
  }
}

/** Para a coluna "P" das tabelas: a função ("VOL"), que cabe no mesmo espaço da posição antiga. */
export function rotuloFuncao(j: Jogador): Funcao {
  return funcaoDe(j);
}
